import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/App";
import { useI18n } from "@/i18n/I18nProvider";
import Webcam from "react-webcam";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SmartDateInput from "@/components/SmartDateInput";
import SupplierAutocomplete from "@/components/SupplierAutocomplete";
import { Camera, Upload, X, Package, Loader2, Image, Star } from "lucide-react";
import { toast } from "sonner";

export default function AddProduct() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const favoriteId = searchParams.get("favorite");
  const webcamRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "", category: "", storage_location: "", expiration_date: "", opening_date: "",
    batch_number: "", supplier: "", supplier_id: "", notes: "", image_url: "",
  });
  const [showCamera, setShowCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [fromFavoriteName, setFromFavoriteName] = useState("");
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [catsRes, locsRes] = await Promise.all([
          api.get("/categories"),
          api.get("/storage-locations"),
        ]);
        setCategories(catsRes.data || []);
        setLocations(locsRes.data || []);
      } catch { toast.error("Error"); }
      finally { setOptionsLoading(false); }
    })();
  }, []);

  // Edit-mode: load product
  useEffect(() => {
    if (!id) return;
    setIsEdit(true);
    (async () => {
      try {
        const response = await api.get(`/products/${id}`);
        const p = response.data;
        setFormData({
          name: p.name || "",
          category: p.category || "",
          storage_location: p.storage_location || "",
          expiration_date: p.expiration_date || "",
          opening_date: p.opening_date || "",
          batch_number: p.batch_number || "",
          supplier: p.supplier || "",
          supplier_id: p.supplier_id || "",
          notes: p.notes || "",
          image_url: p.image_url || "",
        });
        if (p.image_url) setCapturedImage(p.image_url);
      } catch { toast.error("Error"); navigate("/products"); }
    })();
  }, [id, navigate]);

  // Prefill from favorite
  useEffect(() => {
    if (!favoriteId || isEdit) return;
    (async () => {
      try {
        const res = await api.get(`/favorites/${favoriteId}`);
        const f = res.data;
        setFromFavoriteName(f.name);
        setFormData((prev) => ({
          ...prev,
          name: f.name || "",
          category: f.category || "",
          storage_location: f.storage_location || "",
          batch_number: f.batch_number || "",
          supplier: f.supplier || "",
          supplier_id: f.supplier_id || "",
          notes: f.notes || "",
          image_url: f.image_url || "",
        }));
        if (f.image_url) setCapturedImage(f.image_url);
      } catch { toast.error("Error al cargar favorito"); }
    })();
  }, [favoriteId, isEdit]);

  const capture = useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      setCapturedImage(imageSrc);
      setFormData(prev => ({ ...prev, image_url: imageSrc }));
      setShowCamera(false);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.category || !formData.storage_location || !formData.expiration_date) {
      toast.error(t("addProduct.requiredFieldsError") || "Completa los campos obligatorios");
      return;
    }
    setLoading(true);
    try {
      if (isEdit) {
        await api.put(`/products/${id}`, formData);
      } else {
        await api.post("/products", formData);
      }
      navigate("/products");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="add-product-page" className="max-w-3xl mx-auto space-y-6 animate-fade-in">
      <div>
        <h1 className="font-heading font-bold text-2xl text-foreground">
          {isEdit ? t("addProduct.editTitle") : t("addProduct.title")}
        </h1>
        <p className="text-muted-foreground mt-1">
          {isEdit ? t("addProduct.editSubtitle") : t("addProduct.subtitle")}
        </p>
        {fromFavoriteName && (
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-xs text-amber-700" data-testid="from-favorite-banner">
            <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
            Plantilla: <span className="font-semibold">{fromFavoriteName}</span>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Photo */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Image className="h-5 w-5" />{t("addProduct.photoTitle")}
            </CardTitle>
            <CardDescription>{t("addProduct.photoDesc")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {showCamera ? (
              <div className="space-y-4">
                <div className="relative rounded-lg overflow-hidden bg-slate-900">
                  <Webcam ref={webcamRef} screenshotFormat="image/jpeg" className="w-full" videoConstraints={{ facingMode: "environment" }} />
                </div>
                <div className="flex gap-3">
                  <Button type="button" onClick={capture} className="flex-1" data-testid="capture-btn">
                    <Camera className="h-4 w-4 mr-2" />{t("addProduct.capture")}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowCamera(false)}>
                    <X className="h-4 w-4 mr-2" />{t("common.cancel")}
                  </Button>
                </div>
              </div>
            ) : capturedImage ? (
              <div className="space-y-4">
                <div className="relative">
                  <img src={capturedImage} alt="Product" className="w-full rounded-lg max-h-64 object-contain bg-zinc-50" />
                  <Button type="button" variant="secondary" size="sm" className="absolute top-2 right-2"
                    onClick={() => { setCapturedImage(null); setFormData(prev => ({ ...prev, image_url: "" })); }}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-sm text-primary flex items-center gap-2"><Image className="h-4 w-4" />{t("addProduct.photoAttached")}</p>
              </div>
            ) : (
              <div className="flex gap-4">
                <Button type="button" variant="outline" className="flex-1 border-primary text-primary hover:bg-primary/5"
                  onClick={() => setShowCamera(true)} data-testid="open-camera-btn">
                  <Camera className="h-4 w-4 mr-2" />{t("addProduct.openCamera")}
                </Button>
                <label className="flex-1">
                  <Button type="button" variant="outline" className="w-full border-primary text-primary hover:bg-primary/5" asChild>
                    <span><Upload className="h-4 w-4 mr-2" />{t("addProduct.uploadImage")}</span>
                  </Button>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setCapturedImage(reader.result);
                        setFormData(prev => ({ ...prev, image_url: reader.result }));
                      };
                      reader.readAsDataURL(file);
                    }
                  }} />
                </label>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Details */}
        <Card>
          <CardHeader><CardTitle className="font-heading text-lg">{t("addProduct.detailsTitle")}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t("addProduct.name")}</Label>
                <Input id="name" placeholder={t("addProduct.namePlaceholder")} value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} required data-testid="product-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">{t("addProduct.category")}</Label>
                <Select value={formData.category}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                  disabled={optionsLoading || categories.length === 0}>
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder={optionsLoading ? t("common.loading") : t("addProduct.selectCategory")} />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (<SelectItem key={cat.category_id} value={cat.name}>{cat.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="storage">{t("addProduct.storageLocation")}</Label>
                <Select value={formData.storage_location}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, storage_location: value }))}
                  disabled={optionsLoading || locations.length === 0}>
                  <SelectTrigger data-testid="storage-select">
                    <SelectValue placeholder={optionsLoading ? t("common.loading") : t("addProduct.selectLocation")} />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(loc => (<SelectItem key={loc.location_id} value={loc.name}>{loc.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("addProduct.expirationDate")} *</Label>
                <SmartDateInput
                  value={formData.expiration_date}
                  onChange={(iso) => setFormData(prev => ({ ...prev, expiration_date: iso }))}
                  data-testid="expiration-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("addProduct.openingDate")}</Label>
                <SmartDateInput
                  value={formData.opening_date}
                  onChange={(iso) => setFormData(prev => ({ ...prev, opening_date: iso }))}
                  data-testid="opening-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="batch">{t("addProduct.batchNumber")}</Label>
                <Input id="batch" placeholder={t("addProduct.batchPlaceholder")} value={formData.batch_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, batch_number: e.target.value }))} data-testid="batch-input" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>{t("addProduct.supplier")}</Label>
                <SupplierAutocomplete
                  supplierId={formData.supplier_id}
                  supplierName={formData.supplier}
                  onChange={({ supplier_id, supplier }) => setFormData(prev => ({ ...prev, supplier_id, supplier }))}
                  placeholder={t("addProduct.supplierPlaceholder")}
                  data-testid="supplier-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">{t("addProduct.notes")}</Label>
              <Textarea id="notes" placeholder={t("addProduct.notesPlaceholder")} value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))} rows={3} data-testid="notes-input" />
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-4">
          <Button type="submit" disabled={loading} className="flex-1" data-testid="submit-product-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Package className="h-4 w-4 mr-2" />}
            {loading ? t("common.saving") : isEdit ? t("addProduct.update") : t("addProduct.submit")}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate("/products")} data-testid="cancel-btn">{t("common.cancel")}</Button>
        </div>
      </form>
    </div>
  );
}
